<template>
  <ReportCard :title="$t('report.clients_new_vs_returning')">
    <template #header>
      <button @click.stop="showNewReturningInfo = true"
        class="flex items-center justify-center w-4 h-4 text-gray-400 hover:text-blue-500 transition-colors"
        :title="$t('report.clients_new_returning_info.title')">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-4 h-4">
          <path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
        </svg>
      </button>
    </template>
    <div class="flex items-center justify-end mb-2">
      <TogglePill
        :model-value="chartMode === 'absolute'"
        @update:model-value="chartMode = $event ? 'absolute' : 'percent'"
      >
        <template #icon>
          <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
            <path d="M2 12h10M3 9v3M6 6v6M9 3v9M12 1v11" />
          </svg>
        </template>
        {{ $t('chart.mode_absolute') }}
      </TogglePill>
    </div>
    <div class="ct-legend flex items-center justify-center gap-3 mb-1">
      <span class="flex items-center gap-1.5">
        <span class="w-2 h-2 rounded-full" style="background-color: #10B981"></span>
        <span class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          {{ $t('report.clients_new') }}
        </span>
      </span>
      <span class="flex items-center gap-1.5">
        <span class="w-2 h-2 rounded-full" style="background-color: #6366F1"></span>
        <span class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          {{ $t('report.clients_returning') }}
        </span>
      </span>
    </div>
    <div class="relative">
      <div ref="chartEl" class="w-full h-64"></div>
      <div v-if="loading"
        class="absolute inset-0 flex items-center justify-center bg-background/60 backdrop-blur-[1px] rounded-lg transition-opacity">
        <Loading />
      </div>
    </div>
  </ReportCard>

  <BottomSheet v-model:is-open="showNewReturningInfo" variant="sidebar">
    <ClientsNewReturningInfoSheet />
  </BottomSheet>
</template>

<script setup lang="ts">
import { ref, shallowRef, watch, onBeforeUnmount, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import * as echarts from 'echarts'
import type { SalesByClientsResponse } from '../../responses'
import ReportCard from '../ReportCard.vue'
import Loading from '../Loading.vue'
import TogglePill from '../ui/TogglePill.vue'
import BottomSheet from '../BottomSheet.vue'
import ClientsNewReturningInfoSheet from '../ClientsNewReturningInfoSheet.vue'
import { useIsDarkMode } from '../../composables/useIsDarkMode'

const props = defineProps<{
  response: SalesByClientsResponse | null
  formatXLabel: (dateString: string) => string
  loading: boolean
}>()

const { t } = useI18n()

const chartEl = ref<HTMLElement>()
const chartInstance = shallowRef<echarts.ECharts>()
const chartMode = ref<'percent' | 'absolute'>('percent')
const showNewReturningInfo = ref(false)

const isDark = useIsDarkMode()

function buildChartOptions(): echarts.EChartsOption {
  if (!props.response?.intervals) return {}

  // Trim trailing empty intervals
  const rawIntervals = props.response.intervals
  let lastIdx = rawIntervals.length - 1
  while (lastIdx >= 0 && rawIntervals[lastIdx].data.period.new_clients === 0 && rawIntervals[lastIdx].data.period.returning_clients === 0) lastIdx--
  const intervals = lastIdx >= 0 ? rawIntervals.slice(0, lastIdx + 1) : rawIntervals

  const xAxisData = intervals.map(i => props.formatXLabel(i.date))
  const newData = intervals.map(i => i.data.period.new_clients)
  const returningData = intervals.map(i => i.data.period.returning_clients)

  const isPercent = chartMode.value === 'percent'
  const totals = intervals.map(i => i.data.period.new_clients + i.data.period.returning_clients)

  const newSeriesData = isPercent
    ? newData.map((v, i) => totals[i] > 0 ? (v / totals[i]) * 100 : 0)
    : newData
  const returningSeriesData = isPercent
    ? returningData.map((v, i) => totals[i] > 0 ? (v / totals[i]) * 100 : 0)
    : returningData

  return {
    grid: { left: '2%', right: '2%', bottom: '12%', top: '8%', containLabel: true },
    xAxis: {
      type: 'category',
      data: xAxisData,
      axisLine: { lineStyle: { color: isDark.value ? 'rgba(255,255,255,0.15)' : 'rgba(156,163,175,0.3)' } },
      axisTick: { show: false },
      axisLabel: { color: isDark.value ? 'rgba(255,255,255,0.6)' : 'rgba(75,85,99,0.8)', fontSize: 11 },
    },
    yAxis: isPercent
      ? {
          type: 'value', min: 0, max: 100,
          axisLine: { show: false }, axisTick: { show: false },
          splitLine: { lineStyle: { color: isDark.value ? 'rgba(255,255,255,0.08)' : 'rgba(229,231,235,0.4)', type: 'dashed' } },
          axisLabel: { color: isDark.value ? 'rgba(255,255,255,0.6)' : 'rgba(75,85,99,0.8)', fontSize: 11, formatter: (v: number) => v + '%' },
        }
      : {
          type: 'value', minInterval: 1,
          axisLine: { show: false }, axisTick: { show: false }, splitNumber: 4,
          splitLine: { lineStyle: { color: isDark.value ? 'rgba(255,255,255,0.08)' : 'rgba(229,231,235,0.4)', type: 'dashed' } },
          axisLabel: { color: isDark.value ? 'rgba(255,255,255,0.6)' : 'rgba(75,85,99,0.8)', fontSize: 11, formatter: (v: number) => String(Math.round(v)) },
        },
    tooltip: {
      trigger: 'axis',
      backgroundColor: isDark.value ? 'rgba(40,39,39,0.95)' : 'rgba(255,255,255,0.95)',
      borderColor: isDark.value ? 'rgba(255,255,255,0.1)' : 'rgba(229,231,235,0.8)',
      borderWidth: 1, borderRadius: 8, confine: true,
      textStyle: { color: isDark.value ? 'rgba(255,255,255,0.9)' : 'rgba(17,24,39,0.9)', fontSize: 12 },
      formatter: (params: unknown) => {
        if (!Array.isArray(params) || params.length === 0) return ''
        const idx = params[0].dataIndex as number
        const nc = newData[idx] ?? 0
        const rc = returningData[idx] ?? 0
        const total = nc + rc
        const nPct = total > 0 ? ((nc / total) * 100).toFixed(1) : '0.0'
        const rPct = total > 0 ? ((rc / total) * 100).toFixed(1) : '0.0'
        let html = `<div style="font-weight:600;margin-bottom:4px">${params[0].axisValue}</div>`
        html += `<div style="display:flex;align-items:center;gap:6px;margin-top:2px">${params[0].marker}<span>${t('report.clients_new')}</span><span style="margin-left:auto;font-weight:600">${nc}</span><span style="color:rgba(128,128,128,0.8)">(${nPct}%)</span></div>`
        if (params[1]) html += `<div style="display:flex;align-items:center;gap:6px;margin-top:2px">${params[1].marker}<span>${t('report.clients_returning')}</span><span style="margin-left:auto;font-weight:600">${rc}</span><span style="color:rgba(128,128,128,0.8)">(${rPct}%)</span></div>`
        html += `<div style="margin-top:4px;padding-top:4px;border-top:1px solid rgba(128,128,128,0.2);font-weight:600">Total: ${total}</div>`
        return html
      },
    },
    dataZoom: [{ type: 'inside', xAxisIndex: [0] }],
    series: [
      { type: 'bar', name: t('report.clients_new'), stack: 'total', data: newSeriesData, itemStyle: { color: '#10B981' }, emphasis: { focus: 'series' } },
      { type: 'bar', name: t('report.clients_returning'), stack: 'total', data: returningSeriesData, itemStyle: { color: '#6366F1', borderRadius: [2, 2, 0, 0] }, emphasis: { focus: 'series' } },
    ],
  }
}

function refreshChart() {
  if (chartInstance.value) {
    chartInstance.value.setOption(buildChartOptions(), true)
  } else if (chartEl.value) {
    chartInstance.value = echarts.init(chartEl.value)
    chartInstance.value.setOption(buildChartOptions())
  }
}

watch(chartMode, () => refreshChart())
watch(isDark, () => refreshChart())

watch(() => props.response, async () => {
  await nextTick()
  refreshChart()
})

watch(chartEl, async (el) => {
  if (el && !chartInstance.value) {
    await nextTick()
    setTimeout(refreshChart, 300)
  }
}, { immediate: true })

onBeforeUnmount(() => {
  chartInstance.value?.dispose()
})

defineExpose({ refreshChart })
</script>
